import type {
  ProviderAuthenticationCheckpointDetails,
  PublicAccount,
  UniLoginHostedAuthStatusResponse,
  UniLoginProvider,
} from '@unipile/types';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../../../libs/axios';
import type { QueryConfig } from '../../../types/react-query-utility';

/** The Gateway completes the connection while answering, so the status can resolve to the account or a checkpoint. */
export type UniLoginRequestStatus = UniLoginHostedAuthStatusResponse | PublicAccount | ProviderAuthenticationCheckpointDetails;

export async function getUniLoginRequestStatus({
  provider,
  request_id,
}: {
  provider: UniLoginProvider;
  request_id: string;
}): Promise<UniLoginRequestStatus> {
  const response = await axiosInstance.get<UniLoginRequestStatus>(`/v2/auth/unilogin/request/${request_id}`, {
    params: { provider },
  });

  return response.data;
}

export const getUniLoginRequestStatusQueryOptions = (provider: UniLoginProvider, request_id: string) => {
  return queryOptions({
    queryKey: ['unilogin-request-status', provider, request_id],
    queryFn: () => getUniLoginRequestStatus({ provider, request_id }),
  });
};

type UseUniLoginRequestStatusOptions = {
  provider: UniLoginProvider;
  request_id: string | undefined;
  queryConfig?: QueryConfig<typeof getUniLoginRequestStatusQueryOptions>;
};

export function useUniLoginRequestStatus({ provider, request_id, queryConfig }: UseUniLoginRequestStatusOptions) {
  return useQuery({
    ...getUniLoginRequestStatusQueryOptions(provider, request_id ?? ''),
    ...queryConfig,
    enabled: request_id !== undefined && queryConfig?.enabled !== false,
  });
}
